import { useEffect, useMemo, useSyncExternalStore } from "react";
import { getConfig } from "../config";
import { createProxy } from "../helpers/createProxy";
import { matchMedia } from "../helpers/matchMedia";
import { useSafeRef } from "./useSafeRef";
let mediaState = (
  // development only safeguard
  process.env.NODE_ENV === "development" ? createProxy(
    {},
    {
      get(target, key) {
        if (typeof key === "string" && key[0] === "$" && // dont error on $$typeof
        key[1] !== "$") {
          throw new Error(`Access mediaState should not use "$": ${key}`);
        }
        return Reflect.get(target, key);
      }
    }
  ) : {}
);
const mediaQueryConfig = {};
const getMedia = () => mediaState;
const mediaKeys = /* @__PURE__ */ new Set();
const isMediaKey = (key) => mediaKeys.has(key);
let initState;
const getInitialMediaState = () => {
  return (getConfig().disableSSR ? mediaState : initState) || {};
};
let mediaKeysOrdered;
const getMediaKeyImportance = (key) => {
  if (process.env.NODE_ENV === "development" && key[0] === "$") {
    throw new Error("use short key");
  }
  return mediaKeysOrdered.indexOf(key) + 2;
};
const dispose = /* @__PURE__ */ new Set();
const configureMedia = (config) => {
  const { media, mediaQueryDefaultActive } = config;
  if (!media)
    return;
  for (const key in media) {
    mediaState[key] = (mediaQueryDefaultActive == null ? void 0 : mediaQueryDefaultActive[key]) || false;
    mediaKeys.add(`$${key}`);
  }
  Object.assign(mediaQueryConfig, media);
  initState = { ...mediaState };
  updateCurrentState();
  mediaKeysOrdered = Object.keys(media);
  if (config.disableSSR) {
    setupMediaListeners();
  }
};
function unlisten() {
  dispose.forEach((cb) => cb());
  dispose.clear();
}
let configuredKey = "";
function setupMediaListeners() {
  const nextKey = JSON.stringify(mediaQueryConfig);
  if (nextKey === configuredKey)
    return;
  configuredKey = nextKey;
  unlisten();
  for (const key in mediaQueryConfig) {
    let update2 = function() {
      const next = !!getMatch().matches;
      if (next === mediaState[key])
        return;
      mediaState = { ...mediaState, [key]: next };
      updateCurrentState();
    };
    var update = update2;
    const str = mediaObjectToString(mediaQueryConfig[key]);
    const getMatch = () => matchMedia(str);
    const match = getMatch();
    if (!match) {
      throw new Error("\u26A0\uFE0F No match");
    }
    match.addListener(update2);
    dispose.add(() => {
      match.removeListener(update2);
    });
    update2();
  }
}
function useMediaListeners(config) {
  if (config.disableSSR)
    return;
  useEffect(() => {
    setupMediaListeners();
  }, []);
}
const listeners = /* @__PURE__ */ new Set();
let flushing = false;
function updateCurrentState() {
  if (flushing)
    return;
  flushing = true;
  Promise.resolve().then(() => {
    flushing = false;
    listeners.forEach((cb) => cb(mediaState));
  });
}
const shouldUpdate = /* @__PURE__ */ new WeakMap();
function setMediaShouldUpdate(ref, props) {
  return shouldUpdate.set(ref, props);
}
function subscribe(subscriber) {
  listeners.add(subscriber);
  return () => listeners.delete(subscriber);
}
function useMedia(uid, debug) {
  const internal = useSafeRef(void 0);
  if (!internal.current) {
    internal.current = {
      prev: initState
    };
  }
  const state = useSyncExternalStore(
    subscribe,
    () => {
      const { touched, prev } = internal.current;
      const componentState = uid ? shouldUpdate.get(uid) : void 0;
      if ((componentState == null ? void 0 : componentState.enabled) === false) {
        return prev;
      }
      const testKeys = (componentState == null ? void 0 : componentState.keys) ?? ((!componentState || componentState.enabled) && touched ? [...touched] : null);
      const hasntUpdated = !testKeys || (testKeys == null ? void 0 : testKeys.every((key) => mediaState[key] === prev[key]));
      if (hasntUpdated) {
        return prev;
      }
      internal.current.prev = mediaState;
      return mediaState;
    },
    () => initState
  );
  return useMemo(() => {
    return new Proxy(state, {
      get(_, key) {
        if (typeof key === "string") {
          internal.current.touched ||= /* @__PURE__ */ new Set();
          internal.current.touched.add(key);
        }
        return Reflect.get(state, key);
      }
    });
  }, [state]);
}
function useMediaPropsActive(props, opts) {
  const media = useMedia();
  const shouldExpandShorthands = opts == null ? void 0 : opts.expandShorthands;
  return useMemo(() => {
    const config = getConfig();
    const next = {};
    const importancesUsed = {};
    const propNames = Object.keys(props);
    for (let i = propNames.length - 1; i >= 0; i--) {
      let key = propNames[i];
      const val = props[key];
      if (key[0] === "$") {
        const mediaKey = key.slice(1);
        if (!media[mediaKey])
          continue;
        if (val && typeof val === "object") {
          const subKeys = Object.keys(val);
          for (let j = subKeys.length; j--; j >= 0) {
            let subKey = subKeys[j];
            const value = val[subKey];
            if (shouldExpandShorthands) {
              subKey = config.shorthands[subKey] || subKey;
            }
            mergeMediaByImportance(next, mediaKey, subKey, value, importancesUsed);
          }
        }
      } else {
        if (shouldExpandShorthands) {
          key = config.shorthands[key] || key;
        }
        mergeMediaByImportance(next, "", key, val, importancesUsed);
      }
    }
    return next;
  }, [media, props]);
}
const getMediaImportanceIfMoreImportant = (mediaKey, key, importancesUsed) => {
  const importance = getMediaKeyImportance(mediaKey);
  return !importancesUsed[key] || importance > importancesUsed[key] ? importance : null;
};
function mergeMediaByImportance(onto, mediaKey, key, value, importancesUsed) {
  const importance = getMediaImportanceIfMoreImportant(mediaKey, key, importancesUsed);
  if (importance === null) {
    return false;
  }
  importancesUsed[key] = importance;
  onto[key] = value;
  return true;
}
function camelToHyphen(str) {
  return str.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`).toLowerCase();
}
function mediaObjectToString(query) {
  if (typeof query === "string") {
    return query;
  }
  return Object.entries(query).map(([feature, value]) => {
    feature = camelToHyphen(feature);
    if (typeof value === "string") {
      return `(${feature}: ${value})`;
    }
    if (typeof value === "number" && /[height|width]$/.test(feature)) {
      value = `${value}px`;
    }
    return `(${feature}: ${value})`;
  }).join(" and ");
}
export {
  configureMedia,
  getInitialMediaState,
  getMedia,
  getMediaImportanceIfMoreImportant,
  getMediaKeyImportance,
  isMediaKey,
  mediaKeys,
  mediaObjectToString,
  mediaQueryConfig,
  mediaState,
  mergeMediaByImportance,
  setMediaShouldUpdate,
  setupMediaListeners,
  useMedia,
  useMediaListeners,
  useMediaPropsActive
};
//# sourceMappingURL=useMedia.js.map
